import React, { useCallback, useEffect, useState } from 'react';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import { useStateSelector } from '../../../../store/selectors';
import { fetchLoiForm } from '../../../../slices/marketplace/loi-form/loi-forms-api';
import { LoiForm } from '../../../../slices/marketplace/loi-form/loi-form';
import { LoiAndTermsForm } from '../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/LoiAndTermsForm/LoiAndTermsForm';
import { useVendorContractsInit } from '../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/useVendorContractsInit';
import { useOwnerEmploymentAgreementDurationInit } from '../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/useOwnerEmploymentAgreementDurationInit';
import createNotification from '../../../../utils/createNotification';
import Helpers from '../../../../utils/helper';

interface LoiSubmissionQuestionsPopupProps {
    loiFormId: string;
    engagementId: number;
    onClose: () => void;
}

const LoiSubmissionQuestionsPopup = (props: LoiSubmissionQuestionsPopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [formData, setFormData] = useState<LoiForm | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const loadForm = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchLoiForm(axios, props.loiFormId);
            setFormData(data);
        } catch {
            createNotification('Could not load LOI form. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [axios, props.loiFormId]);

    useEffect(() => {
        loadForm();
    }, [loadForm]);

    const noOp = useCallback(() => {}, []);

    const {
        hasOwnerEmploymentAgreement,
        columns: ownerEmploymentColumns,
        rowDisplayData: ownerEmploymentRowDisplayData,
        tableRows: ownerEmploymentTableRows,
    } = useOwnerEmploymentAgreementDurationInit({
        engagementId: props.engagementId,
        termForm: formData?.termForm,
        onChange: noOp,
        axios,
    });

    const {
        hasVendorContracts,
        vendorContractColumns,
        vendorContractRowDisplayData,
        vendorContractTableRows,
        vendorContractProjectId,
        vendorContractTableId,
        vendorContractFileColumnName,
        vendorContractFileColumnId,
    } = useVendorContractsInit({
        engagementId: props.engagementId,
        termForm: formData?.termForm,
        onChange: noOp,
        axios,
    });

    return (
        <Rodal
            visible={true}
            onClose={props.onClose}
            animation="slideUp"
            showMask={true}
            width={900}
            showCloseButton={true}
            customStyles={{ height: 'auto' }}>
            <ModalHeader>LOI Submission Questions</ModalHeader>
            <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                {isLoading ? (
                    Helpers.renderTableLoader()
                ) : !formData ? (
                    <div className="text-danger">No LOI form data available.</div>
                ) : (
                    <LoiAndTermsForm
                        formData={formData}
                        isReadonly={true}
                        isInitialQuestionsReadonly={true}
                        isQuestionsOnly={true}
                        onChange={() => {}}
                        sellerLocations={formData.sellerLocations ?? []}
                        hasOwnerEmploymentAgreement={hasOwnerEmploymentAgreement}
                        ownerEmploymentColumns={ownerEmploymentColumns}
                        ownerEmploymentRowDisplayData={ownerEmploymentRowDisplayData}
                        ownerEmploymentTableRows={ownerEmploymentTableRows}
                        hasVendorContracts={hasVendorContracts}
                        vendorContractColumns={vendorContractColumns}
                        vendorContractRowDisplayData={vendorContractRowDisplayData}
                        vendorContractTableRows={vendorContractTableRows}
                        vendorContractProjectId={vendorContractProjectId}
                        vendorContractTableId={vendorContractTableId}
                        vendorContractFileColumnName={vendorContractFileColumnName}
                        vendorContractFileColumnId={vendorContractFileColumnId}
                    />
                )}
            </ModalBody>
        </Rodal>
    );
};

export default LoiSubmissionQuestionsPopup;
